// Ã‚Â© 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
// Presentation fixtures are editable alongside mechanisms, but remain lamps in saved machines.
import type {MachineDefinition,MachineDevice,MachineLamp,DeviceKind,Point2,DeviceAnchor,MachineRule} from '@core/machine'
import {deviceAnchors as anchors,devicePosition as position,moveDevice as move,withAnchor as anchor} from '@core/machine'
import {flasherMountFits} from '../world/flasherMount'
export type FlasherPart=MachineLamp & {readonly kind:'flasher';readonly position:Point2}
export type EditorPart=MachineDevice|FlasherPart
export type PartKind=DeviceKind|'flasher'
export const editableParts=(d:MachineDefinition):readonly EditorPart[]=>[...(d.devices??[]),...(d.lamps??[]).filter((l):l is FlasherPart=>l.kind==='flasher'&&l.position!==undefined)]
export const deviceAnchors=(p:EditorPart):readonly DeviceAnchor[]=>p.kind==='flasher'?[{role:'position',at:p.position,shapes:false}]:anchors(p)
export const devicePosition=(p:EditorPart):Point2|undefined=>p.kind==='flasher'?p.position:position(p)
export const withAnchor=(p:EditorPart,role:string,at:Point2):EditorPart=>p.kind==='flasher'?(role==='position'?{...p,position:at}:p):anchor(p,role,at)
export const moveDevice=(p:EditorPart,x:number,y:number):EditorPart=>p.kind==='flasher'?{...p,position:[Number((p.position[0]+x).toFixed(7)),Number((p.position[1]+y).toFixed(7))]}:move(p,x,y)
export function storeLamp(d:MachineDefinition,lamp:FlasherPart,remove=false):MachineDefinition{
 const previous=(d.lamps??[]).find(l=>l.id===lamp.id),found=previous!==undefined,baseId=lamp.id+'Base'
 const lamps=(d.lamps??[]).filter(l=>l.id!==lamp.id);if(!remove)lamps.push(lamp)
 const colliders=d.colliders.filter(c=>c.id!==baseId)
 if(!remove&&(!found||!flasherMountFits(d,lamp)))colliders.push({id:baseId,kind:'circle',layer:d.layers.find(l=>l.kind==='plane')?.id??'playfield',centre:lamp.position,radius:(lamp.radius??.026)*.027/.026,material:'metal'})
 const prefix=lamp.id+'Builder',timer=prefix+'Pulse'
 const refresh=found&&JSON.stringify(previous.position)!==JSON.stringify(lamp.position)&&(d.rules?.rules??[]).some(r=>r.id.startsWith(prefix))
 if(found&&!remove&&!refresh)return {...d,lamps,colliders}
 const rules=(d.rules?.rules??[]).filter(r=>!r.id.startsWith(prefix)),timers=(d.rules?.timers??[]).filter(t=>t.id!==timer)
 if(!remove){
  const action=(state:'on'|'off'|'strobe')=>({lamp:{id:lamp.id,state}})
  const rule=(suffix:string,on:string,actions:MachineRule['do'])=>rules.push({id:prefix+suffix,on,do:actions})
  rule('Start','ball:start',[action('on')])
  const nearest=(d.devices??[]).filter(p=>p.kind!=='flipper'&&'switch' in p&&p.switch!==undefined&&position(p)!==undefined).sort((a,b)=>{
   const pa=position(a)!,pb=position(b)!;return Math.hypot(pa[0]-lamp.position[0],pa[1]-lamp.position[1])-Math.hypot(pb[0]-lamp.position[0],pb[1]-lamp.position[1])
  })[0]
  if(nearest&&'switch' in nearest){
   timers.push({id:timer,durationMs:180});rule('Hit','switch:'+nearest.switch,[action('strobe'),{startTimer:timer}]);rule('Settle','timer:'+timer+':expire',[action('on')]);rule('End','ball:end',[action('off'),{stopTimer:timer}])
  }else rule('End','ball:end',[action('off')])
 }
 const result={...d,lamps,colliders,rules:{...d.rules,rules,timers}}
 return remove?withoutLampReferences(result,lamp):result
}

/** Remove only the deleted fixture's commands; scoring and other mode actions retain order. */
function withoutLampReferences(d:MachineDefinition,lamp:FlasherPart):MachineDefinition{
 const emptyGroup=lamp.group!==undefined&&!d.lamps?.some(l=>l.group===lamp.group)
 const prune=(v:unknown):unknown=>{
  if(Array.isArray(v))return v.map(prune).filter(x=>x!==undefined)
  if(v===null||typeof v!=='object')return v
  const o=v as Record<string,unknown>
  if((o['lamp'] as {id?:string}|undefined)?.id===lamp.id||(emptyGroup&&(o['lampGroup'] as {id?:string}|undefined)?.id===lamp.group))return undefined
  if(o['lampState']===lamp.id)return 0
  const result:Record<string,unknown>={}
  for(const [key,value]of Object.entries(o)){
   const next=prune(value)
   if(next===undefined)continue
   if(['onStart','onShot','onComplete','onTimeout'].includes(key)&&Array.isArray(next)&&next.length===0)continue
   result[key]=next
  }
  if(Array.isArray(result['do'])&&result['do'].length===0)return undefined
  return result
 }
 const devices=d.devices.map(p=>'lamp' in p&&p.lamp===lamp.id?Object.fromEntries(Object.entries(p).filter(([key])=>key!=='lamp')) as unknown as MachineDevice:p)
 return {...d,devices,...(d.rules?{rules:prune(d.rules) as NonNullable<MachineDefinition['rules']>}:{})}
}
